"use client";

import { useCallback, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { useSearchParams } from "next/navigation";
import { MagicLinkPurpose } from "@rmh/defs";
import { getValidPatientDestination } from "@rmh/utils";
import { SignInSearchParams } from "@/features/auth/types";
import { OnboardingHeader } from "@/features/onboardingV2/components/layout/OnboardingHeader";
import { OnboardingShell } from "@/features/onboardingV2/components/layout/OnboardingShell";
import {
  MagicSignInRequestForm,
  type MagicSignInRequestValues,
} from "@/features/onboardingV2/components/forms/auth/MagicSignInRequestForm";
import { MagicLinkSentPanel } from "@/features/onboardingV2/components/forms/auth/MagicLinkSentPanel";
import { requestMagicLink } from "@/features/onboardingV2/utils/requestMagicLink";
import { LoadingPage } from "@rmh/ui/src/components";
import { isStaffEmail } from "@/features/impersonation/utils/staffEmail";
import { useStaffSsoSignIn } from "@/features/impersonation/hooks/useStaffSsoSignIn";

type Step = "request" | "checkEmail";

export default function Page() {
  const t = useTranslations("onboardingV2.magicSignIn");
  const tSso = useTranslations("impersonation.signIn");
  const searchParams = useSearchParams();
  const [step, setStep] = useState<Step>("request");
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Guards against a double-submit: the submit button is disabled while
  // submitting, but `isSubmitting` only disables it on the next render, so a
  // fast double-tap / Enter+click could otherwise fire requestMagicLink twice
  // (two emails).
  const isSubmittingRef = useRef(false);

  const handleSsoFailure = useCallback(() => setError(tSso("error")), [tSso]);
  const {
    isOidcCallback,
    isAvailable: isSsoAvailable,
    startStaffSso,
  } = useStaffSsoSignIn(handleSsoFailure);

  // The deep link the middleware bounced the visitor off, if it survives
  // validation. The lambda validates it again before it reaches the email.
  const callbackUrl =
    getValidPatientDestination(
      searchParams?.get(SignInSearchParams.CallbackUrl)
    ) ?? undefined;

  /**
   * One entry point for everyone. The address decides the flow: staff hand off
   * to the identity provider, patients get a magic link.
   *
   * The domain is only a routing hint — it decides which flow to offer, not
   * who anyone is. Authentication still happens at the provider, and the
   * middleware reads the real Cognito groups before anyone reaches the patient
   * picker. Staff accounts are SSO-only anyway (the `preTokenGeneration`
   * trigger refuses any other source for them), so sending them down the
   * magic-link path would just produce a link that cannot be redeemed.
   */
  const handleSubmit = async (values: MagicSignInRequestValues) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      setError(undefined);

      // `isSsoAvailable` is false on stacks with no OIDC provider (local Dev),
      // where staff are ordinary Cognito users — fall through to the normal
      // flow there rather than dead-ending them.
      if (isStaffEmail(values.identifier) && isSsoAvailable) {
        await startStaffSso();
        return;
      }

      await requestMagicLink(
        values.identifier,
        MagicLinkPurpose.SignIn,
        callbackUrl
      );
      setEmail(values.identifier);
      setStep("checkEmail");
    } catch (submitError) {
      console.error("Error starting sign-in", submitError);
      setError(
        isStaffEmail(values.identifier)
          ? tSso("error")
          : t("request.error.generic")
      );
    } finally {
      setIsSubmitting(false);
      isSubmittingRef.current = false;
    }
  };

  // Coming back from the identity provider. Amplify is exchanging the code and
  // will navigate on, so showing the form again would be a flash of the thing
  // the visitor has already finished with.
  if (isOidcCallback && !error) {
    return <LoadingPage />;
  }

  return (
    <OnboardingShell header={<OnboardingHeader />}>
      {step === "request" && (
        <MagicSignInRequestForm
          error={error}
          isSubmitting={isSubmitting}
          initialEmail={email}
          onSubmit={handleSubmit}
        />
      )}
      {step === "checkEmail" && (
        <MagicLinkSentPanel
          email={email}
          onResend={() =>
            requestMagicLink(email, MagicLinkPurpose.SignIn, callbackUrl)
          }
          onEdit={() => setStep("request")}
        />
      )}
    </OnboardingShell>
  );
}
